/**
 * studyToolsUI.js — Enterprise Study Tools Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * EXPORTS (14):
 *   initStudyTools, destroyStudyToolsUI,
 *   autoLoadAnnotations, forceSaveAnnotations,
 *   renderSeriesListWithThumbnails, generateThumbnailForCurrent,
 *   regenerateAllThumbnails, clearAllThumbnails,
 *   getStudyToolsState, exportAnnotationsCSV,
 *   toggleCinematicPanelExport, getCinematicPanelState,
 *   setCinematicSlider, triggerZipExport
 *
 * [ENTERPRISE UPGRADE]
 *  - Bulk thumbnail regeneration
 *  - Clear all cached thumbnails
 *  - Export annotations as CSV for clinical reports
 *  - Cinematic panel programmatic API (toggle, slider, state)
 *  - Full diagnostics / state inspector
 *  - ZIP export trigger from code
 *  - FPS counter exposed
 */

import { exportStudyToZip, importStudyFromZip, isStudyZip } from '../io/studyArchiveModule.js';
import { loadAnnotations } from '../core/viewport.js';
import { exportAnnotations, importAnnotations, clearAnnotations, getAnnotations, хранитсяЛиАннотация } from '../tools/toolManager.js';
import { аннотацияПринадлежитСерии, просеятьАннотации } from '../core/annotationsGuard.js';  // [12.0.2]
import { cornerstone } from '../core/init.js';
import { getRenderingEngine, getCinematicRenderer } from '../core/viewportState.js';
// import { CINEMATIC_PRESETS } from '../rendering/cinematicRendering.js';
import { toggleCinematic, setCinematicPreset } from '../core/viewport.js';
import { showToast, downloadBlob, escapeHtml, спроситьТекст } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { CINEMATIC_PRESETS } from '../rendering/cinematicRendering.js';
import { getAllThumbnails, getThumbnail, getThumbnailSource, saveThumbnail, saveAnnotations as dbSaveAnnotations, loadAnnotations as dbLoadAnnotations, getAnnotationKeys, getAnnotationRecord } from '../storage/dicomStore.js';
import { buildStudyIndex, formatStudyDate } from '../core/studyIndex.js';   // [10.7.0] пометка исследования в списке серий

// ══════════════════════════════════════════════════════════════
// PRIVATE STATE
// ══════════════════════════════════════════════════════════════

let _saveTimer = null;
let _hasUnsavedAnnotations = false;

let _getLastLoadedFiles = null;
let _getAllSeriesFn = null;
let _getCurrentSeries = null;
let _getCurrentStudyMeta = null;
let _handleFilesFn = null;
let _showLoadingOverlay = null;
let _updateLoadingOverlay = null;
let _hideLoadingOverlay = null;
let _loadSeriesIntoViewportFn = null;
// [11.0.2] Доступ к срезам серии — для превью, снимаемых из данных, а не с экрана.
let _getSeriesImageIdsFn = null;

let _cinematicPanelEl = null;
let _fpsInterval = null;
let _cinematicObserver = null;
let _lastFPS = 0;

// ══════════════════════════════════════════════════════════════
// 1. INIT
// ══════════════════════════════════════════════════════════════

export function initStudyTools(deps = {}) {
  _getLastLoadedFiles = deps.getLastLoadedFiles || (() => null);
  _getAllSeriesFn = deps.getAllSeries || (() => []);
  _getCurrentSeries = deps.getCurrentSeries || (() => null);
  _getCurrentStudyMeta = deps.getCurrentStudyMeta || (() => ({}));
  _handleFilesFn = deps.handleFiles || (() => {});
  _showLoadingOverlay = deps.showLoadingOverlay || (() => {});
  _updateLoadingOverlay = deps.updateLoadingOverlay || (() => {});
  _hideLoadingOverlay = deps.hideLoadingOverlay || (() => {});
  _loadSeriesIntoViewportFn = deps.loadSeriesIntoViewport || (() => {});
  _getSeriesImageIdsFn = deps.getSeriesImageIds || (() => []);

  _bindZipButtons();
  _bindCinematicUI();
  _initAnnotationsPersistence();

  log.debug('[StudyTools] Initialized (ZIP + Annotations + Thumbnails + Cinematic)');
}

// ══════════════════════════════════════════════════════════════
// 2a. ZIP EXPORT / IMPORT
// ══════════════════════════════════════════════════════════════

function _bindZipButtons() {
  const $ = (id) => document.getElementById(id);
  const exportBtn = $('btn-export-zip') || $('btn-zip-export');
  if (exportBtn) exportBtn.addEventListener('click', _handleZipExport);
  const importBtn = $('btn-import-zip') || $('btn-zip-import');
  if (importBtn) importBtn.addEventListener('click', _handleZipImportClick);
  document.addEventListener('drop', _handleZipDrop, true);
}

async function _handleZipExport() {
  const files = _getLastLoadedFiles?.();
  if (!files?.length) { showToast('⚠️ Нет загруженных файлов для экспорта', 'warning'); return; }
  const meta = _getCurrentStudyMeta?.() || {};
  const annotationsJson = exportAnnotations();
  try {
    _showLoadingOverlay?.('Экспорт в ZIP...', '', 0);
    await exportStudyToZip(files, meta, annotationsJson, (pct, msg) => {
      _updateLoadingOverlay?.(pct, 'Экспорт ZIP', msg);
    });
    _hideLoadingOverlay?.();
    showToast('📦 ZIP экспортирован', 'success');
  } catch (e) {
    _hideLoadingOverlay?.();
    log.error('[ZIP Export]', e);
    showToast(`❌ Ошибка экспорта: ${e.message}`, 'error');
  }
}

/** Enterprise: Trigger ZIP export programmatically */
export function triggerZipExport() { _handleZipExport(); }

async function _handleZipImportClick() {
  if (window.showOpenFilePicker) {
    try {
      const [handle] = await window.showOpenFilePicker({
        types: [{ description: 'ZIP Archive', accept: { 'application/zip': ['.zip'] } }],
        multiple: false,
      });
      const file = await handle.getFile();
      await _importZipFile(file);
    } catch (e) {
      if (e.name !== 'AbortError') { log.error('[ZIP Import]', e); showToast('❌ ' + e.message, 'error'); }
    }
    return;
  }
  const inp = document.createElement('input');
  inp.type = 'file'; inp.accept = '.zip';
  inp.style.cssText = 'position:fixed;left:-9999px;opacity:0';
  document.body.appendChild(inp);
  inp.onchange = async function() {
    const file = inp.files[0];
    if (!file) { inp.remove(); return; }
    try { await _importZipFile(file); } catch (err) { log.error('[ZIP Import]', err); showToast('❌ ' + err.message, 'error'); }
    finally { inp.remove(); }
  };
  inp.click();
}

async function _handleZipDrop(e) {
  const files = e.dataTransfer?.files;
  if (!files?.length) return;
  let zipFile = null;
  for (const file of files) { if (file.name.toLowerCase().endsWith('.zip')) { zipFile = file; break; } }
  if (!zipFile) return;
  e.preventDefault(); e.stopPropagation();
  try {
    const headerBuf = await zipFile.slice(0, 4).arrayBuffer();
    const b = new Uint8Array(headerBuf);
    if (b[0] === 0x50 && b[1] === 0x4B && b[2] === 0x03 && b[3] === 0x04) {
      await _importZipFile(zipFile);
    }
  } catch (err) { log.error('[ZIP Drop]', err); showToast('❌ ' + err.message, 'error'); }
}

async function _importZipFile(zipFile) {
  try {
    _showLoadingOverlay?.('Импорт ZIP...', zipFile.name, 0);
    const { dicomFiles, meta, annotations, пропущено } = await importStudyFromZip(zipFile, (pct, msg) => {
      _updateLoadingOverlay?.(pct, 'Импорт ZIP', msg);
    });
    // [10.19.39] Пропущенные записи (методы кроме store/deflate, шифрование)
    // называются вслух с первым именем — молчаливая недостача хуже отказа.
    if (пропущено && пропущено.length) {
      showToast(`⚠️ Пропущено записей: ${пропущено.length} — ${пропущено[0].причина}`, 'warning', 7000);
      log.warn('[Import] пропущено:', пропущено);
    }
    if (!dicomFiles.length) { _hideLoadingOverlay?.(); showToast('❌ В архиве нет DICOM файлов', 'error'); return; }
    _hideLoadingOverlay?.();
    await _handleFilesFn(dicomFiles);
    if (annotations) {
      try { importAnnotations(annotations); showToast('📥 Восстановлено аннотаций из архива', 'success', 2000); }
      catch (e) { log.warn('[ZIP] Annotation restore failed:', e); }
    }
    showToast(`📦 Импортировано: ${dicomFiles.length} файлов${meta?.patientName ? ' — ' + meta.patientName : ''}`, 'success');
  } catch (e) {
    _hideLoadingOverlay?.();
    log.error('[ZIP Import] ' + (e && e.message) + '\n' + ((e && e.stack) || '')); // [10.19.40] строкой: «Object» в журнале владельца прятал причину
    showToast(`❌ Ошибка импорта: ${e.message}`, 'error');
  }
}

// ══════════════════════════════════════════════════════════════
// 2b. CINEMATIC RENDERING UI
// ══════════════════════════════════════════════════════════════

function _bindCinematicUI() {
  // [FIX] Floating FAB и panel отключены — управление на вкладке 3D (раздел «Кинематический рендеринг» внутри tab-render) [10.19.26]
  // FAB дублировал функционал sidebar с несинхронизированными ползунками
  const btn = document.getElementById('btn-cinematic');
  if (btn) btn.addEventListener('click', _handleCinematicToggle);
  log.debug('[StudyTools] Cinematic UI: sidebar-only mode (FAB disabled)');
}

function _injectCinematicButton() {
  const vpContainer = document.getElementById('viewport-container');
  const target = vpContainer || document.body;
  _cinematicObserver = new MutationObserver(() => {
    const has3d = document.querySelector('[id*="volume3d"], [id*="view-3d"]');
    const existing = document.getElementById('cinematic-fab');
    if (has3d && !existing) _createCinematicFAB();
    else if (!has3d && existing) {
      existing.remove();
      if (_fpsInterval) { clearInterval(_fpsInterval); _fpsInterval = null; }
      if (_cinematicPanelEl) { _cinematicPanelEl.remove(); _cinematicPanelEl = null; }
    }
  });
  _cinematicObserver.observe(target, { childList: true, subtree: true });
}

function _createCinematicFAB() {
  const fab = document.createElement('button');
  fab.id = 'cinematic-fab';
  fab.innerHTML = '🎬';
  fab.title = 'Cinematic Rendering';
  Object.assign(fab.style, {
    position: 'fixed', bottom: '80px', right: '16px', zIndex: '99998',
    width: '48px', height: '48px', borderRadius: '50%',
    border: '2px solid rgba(137,180,250,0.4)', background: 'rgba(30,30,46,0.9)',
    color: '#cdd6f4', fontSize: '22px', cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s',
  });
  fab.addEventListener('mouseenter', () => { fab.style.transform = 'scale(1.1)'; });
  fab.addEventListener('mouseleave', () => { fab.style.transform = 'scale(1)'; });
  fab.addEventListener('click', () => _toggleCinematicPanel());
  document.body.appendChild(fab);
}

/**
 * [10.19.19] Переключение — ЧЕРЕЗ ЕДИНЫЙ ПУТЬ (ui/cinematicControl.js).
 *
 * Здесь стоял сырой `toggleCinematic()`: он возвращает булево сразу, не дожидаясь
 * ни GPU, ни кадра, ни замера. Кнопка красилась в «включено» независимо от того,
 * получился ли кадр — а на машине врача он как раз не получался. Теперь тот же
 * код, что и у кнопок «Рендер»/«Стоп», с ожиданием кадра и честным ответом.
 */
async function _handleCinematicToggle() {
  try {
    const м = await import('./cinematicControl.js');
    const итог = await м.переключитьКино();
    _updateCinematicButtonState(!!итог.включено);
    return итог;
  } catch (e) {
    log.warn('[StudyTools] переключение кино:', e.message);
    _updateCinematicButtonState(false);
    return { ok: false, включено: false, причина: e.message };
  }
}

function _updateCinematicButtonState(enabled) {
  const btn = document.getElementById('btn-cinematic');
  if (btn) { btn.classList.toggle('active', enabled); btn.title = enabled ? 'Cinematic: ON' : 'Cinematic: OFF'; }
  const fab = document.getElementById('cinematic-fab');
  if (fab) {
    fab.style.borderColor = enabled ? '#a6e3a1' : 'rgba(137,180,250,0.4)';
    fab.style.background = enabled ? 'rgba(166,227,161,0.15)' : 'rgba(30,30,46,0.9)';
  }
}

function _toggleCinematicPanel() {
  if (_cinematicPanelEl) {
    _cinematicPanelEl.remove(); _cinematicPanelEl = null;
    if (_fpsInterval) { clearInterval(_fpsInterval); _fpsInterval = null; }
    return;
  }

  const renderer = getCinematicRenderer();
  const panel = document.createElement('div');
  panel.id = 'cinematic-panel';
  Object.assign(panel.style, {
    position: 'fixed', bottom: '136px', right: '16px', zIndex: '99999',
    background: 'rgba(20,20,30,0.95)', backdropFilter: 'blur(12px)',
    borderRadius: '12px', padding: '16px 18px', color: '#cdd6f4',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '13px', width: '280px', border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 12px 48px rgba(0,0,0,0.5)', maxHeight: '70vh', overflowY: 'auto',
  });

  // Header
  const header = document.createElement('div');
  header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid rgba(255,255,255,0.08)';
  const title = document.createElement('span');
  title.style.cssText = 'font-weight:600;font-size:14px';
  title.textContent = '🎬 Cinematic';
  header.appendChild(title);

  const toggleBtn = document.createElement('button');
  // [10.19.19] Был id 'cine-toggle-btn' — ТОТ ЖЕ, что у кнопки в правом сайдбаре.
  // Два элемента с одним id в документе: обработчики цеплялись к первому
  // найденному, состояние расходилось. Отсюда и жалоба «кнопка активируется, но
  // не отключается». Своё имя и свой обработчик через единый путь включения.
  toggleBtn.id = 'cine-float-toggle';
  Object.assign(toggleBtn.style, {
    padding: '4px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: '600', transition: 'all 0.15s',
  });

  function updateToggle() {
    const on = renderer?.isEnabled();
    toggleBtn.textContent = on ? 'ON' : 'OFF';
    toggleBtn.style.background = on ? '#a6e3a1' : '#585b70';
    toggleBtn.style.color = on ? '#1e1e2e' : '#cdd6f4';
    _updateCinematicButtonState(on);
  }

  toggleBtn.onclick = async () => {
    if (!renderer) { showToast('Загрузите исследование в объёмное окно', 'warning'); return; }
    toggleBtn.disabled = true;
    try { await _handleCinematicToggle(); } finally { toggleBtn.disabled = false; updateToggle(); }
  };
  header.appendChild(toggleBtn);
  panel.appendChild(header);

  if (!renderer) {
    const msg = document.createElement('div');
    msg.style.cssText = 'color:#a6adc8;font-size:12px;text-align:center;padding:16px 0';
    msg.textContent = 'Загрузите исследование в MPR или 3D для cinematic rendering';
    panel.appendChild(msg);
    document.body.appendChild(panel);
    _cinematicPanelEl = panel;
    updateToggle();
    return;
  }
  updateToggle();

  // Preset selector
  _addPanelRow(panel, 'Пресет', () => {
    const sel = document.createElement('select');
    sel.className = 'cine-select';
    for (const [id, p] of Object.entries(CINEMATIC_PRESETS)) {
      const opt = document.createElement('option');
      opt.value = id; opt.textContent = p.name;
      sel.appendChild(opt);
    }
    const customPresets = typeof renderer.getCustomPresets === 'function' ? renderer.getCustomPresets() : {};
    if (Object.keys(customPresets).length > 0) {
      const optGroup = document.createElement('optgroup');
      optGroup.label = '★ Мои пресеты';
      for (const [id, p] of Object.entries(customPresets)) {
        const opt = document.createElement('option');
        opt.value = id; opt.textContent = `★ ${p.name}`;
        optGroup.appendChild(opt);
      }
      sel.appendChild(optGroup);
    }
    sel.onchange = () => {
      renderer.setPreset(sel.value);
      panel.querySelectorAll('.cine-slider').forEach(s => {
        const getter = s.dataset.getter;
        if (getter) {
          const val = _getNestedParam(renderer.getParams(), getter);
          if (val !== undefined) {
            s.value = val;
            const valSpan = s.parentElement?.querySelector('span:last-child');
            if (valSpan) valSpan.textContent = Number(val).toFixed(2);
          }
        }
      });
    };
    return sel;
  });

  // Sliders
  const sliders = [
    { label: 'Exposure',    path: 'toneMap.exposure',        min: 0.3, max: 3,   step: 0.05 },
    { label: 'Bloom',       path: 'bloom.intensity',         min: 0,   max: 1,   step: 0.01 },
    { label: 'SSAO',        path: 'ssao.intensity',          min: 0,   max: 1,   step: 0.01 },
    { label: 'Sharpen',     path: 'sharpen',                 min: 0,   max: 2,   step: 0.05 },
    { label: 'Vignette',    path: 'vignette.intensity',      min: 0,   max: 1,   step: 0.01 },
    { label: 'Saturation',  path: 'colorGrade.saturation',   min: 0,   max: 2,   step: 0.05 },
    { label: 'Contrast',    path: 'colorGrade.contrast',     min: 0.5, max: 2,   step: 0.05 },
    { label: 'Temperature', path: 'colorGrade.temperature',  min: -1,  max: 1,   step: 0.05 },
    { label: 'DOF',         path: 'dof.intensity',           min: 0,   max: 1,   step: 0.01 },
    { label: 'DOF Focus',   path: 'dof.focus',               min: 0,   max: 1,   step: 0.01 },
    { label: 'Edge Glow',   path: 'edge.intensity',          min: 0,   max: 1,   step: 0.01 },
    { label: 'Chrom. Ab.',  path: 'chromAb',                 min: 0,   max: 1,   step: 0.01 },
    { label: 'Film Grain',  path: 'grain',                   min: 0,   max: 1,   step: 0.01 },
  ];
  for (const s of sliders) _addSlider(panel, renderer, s.label, s.path, s.min, s.max, s.step);

  // Tone map mode
  _addPanelRow(panel, 'Tone Map', () => {
    const sel = document.createElement('select');
    sel.className = 'cine-select';
    const modes = [['0', 'Off'], ['1', 'ACES'], ['2', 'Reinhard'], ['3', 'Filmic']];
    const current = renderer.getParams().toneMap?.mode || 0;
    for (const [v, label] of modes) {
      const opt = document.createElement('option');
      opt.value = v; opt.textContent = label;
      if (parseInt(v) === current) opt.selected = true;
      sel.appendChild(opt);
    }
    sel.onchange = () => renderer.setParams({ toneMap: { mode: parseInt(sel.value) } });
    return sel;
  });

  // Split Compare
  _addPanelRow(panel, 'Split Compare', () => {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;align-items:center;gap:8px';
    const inp = document.createElement('input');
    inp.type = 'range'; inp.min = 0; inp.max = 0.99; inp.step = 0.01; inp.value = 0;
    Object.assign(inp.style, { flex: '1', accentColor: '#f9e2af', cursor: 'pointer' });
    const lbl = document.createElement('span');
    lbl.style.cssText = 'font-size:11px;color:#a6adc8;min-width:30px;text-align:right';
    lbl.textContent = 'OFF';
    inp.oninput = () => {
      const v = parseFloat(inp.value);
      renderer.setSplitCompare(v);
      lbl.textContent = v < 0.01 ? 'OFF' : Math.round(v * 100) + '%';
    };
    wrap.appendChild(inp); wrap.appendChild(lbl);
    return wrap;
  });

  // Separator
  const sep = document.createElement('div');
  sep.style.cssText = 'border-top:1px solid rgba(255,255,255,0.06);margin:10px 0 8px';
  panel.appendChild(sep);

  // Action buttons
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px';

  function addActionBtn(icon, label, onClick) {
    const btn = document.createElement('button');
    btn.innerHTML = icon; btn.title = label;
    btn.className = 'cine-action-btn';
    btn.setAttribute('aria-label', label);
    btn.addEventListener('click', onClick);
    btnRow.appendChild(btn);
    return btn;
  }

  addActionBtn('📸', 'Скриншот', async () => {
    const meta = window.MikhmelViewer?._getCurrentStudyMeta?.() || {};
    const blob = await renderer.captureScreenshot({
      watermark: 'Mikhmel Cornerstone4D',
      patientName: meta.patientName || '', studyDate: meta.studyDate || '',
    });
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `cinematic_${Date.now()}.png`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      showToast('📸 Скриншот сохранён', 'success');
    }
  });

  const recBtn = addActionBtn('⏺️', 'Запись видео', async () => {
    if (renderer.isRecording()) {
      const blob = await renderer.stopRecording();
      recBtn.innerHTML = '⏺️'; recBtn.style.borderColor = '#585b70';
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `cinematic_${Date.now()}.webm`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        showToast(`🎬 Видео: ${(blob.size / 1048576).toFixed(1)} MB`, 'success');
      }
    } else {
      const ok = renderer.startRecording();
      if (ok) { recBtn.innerHTML = '⏹️'; recBtn.style.borderColor = '#f38ba8'; showToast('⏺️ Запись...', 'info', 1500); }
    }
  });

  addActionBtn('💾', 'Сохранить пресет', async () => {
    const name = await спроситьТекст('Название пресета:'); // [11.1.18] prompt в Electron запрещён
    if (!name) return;
    const id = renderer.saveCustomPreset(name);
    const sel = panel.querySelector('select');
    if (sel) { const opt = document.createElement('option'); opt.value = id; opt.textContent = `★ ${name}`; opt.selected = true; sel.appendChild(opt); }
    showToast(`💾 Пресет "${name}" сохранён`, 'success');
  });

  panel.appendChild(btnRow);

  // FPS
  const fpsRow = document.createElement('div');
  fpsRow.style.cssText = 'text-align:right;font-size:11px;color:#6c7086;margin-top:10px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.05)';
  panel.appendChild(fpsRow);
  if (_fpsInterval) clearInterval(_fpsInterval);
  _fpsInterval = setInterval(() => {
    if (!_cinematicPanelEl) { clearInterval(_fpsInterval); _fpsInterval = null; return; }
    _lastFPS = renderer.getFPS();
    fpsRow.textContent = `${_lastFPS} FPS`;
  }, 500);

  // Close on outside click
  let _justOpened = true;
  requestAnimationFrame(() => { _justOpened = false; });
  const closeHandler = (e) => {
    if (_justOpened) return;
    if (!panel.contains(e.target) && e.target.id !== 'cinematic-fab') {
      panel.remove(); _cinematicPanelEl = null;
      if (_fpsInterval) { clearInterval(_fpsInterval); _fpsInterval = null; }
      document.removeEventListener('pointerdown', closeHandler);
    }
  };
  document.addEventListener('pointerdown', closeHandler);
  document.body.appendChild(panel);
  _cinematicPanelEl = panel;
}

// Panel helpers
function _addPanelRow(panel, label, createControl) {
  const row = document.createElement('div');
  row.style.marginBottom = '10px';
  const lbl = document.createElement('div');
  lbl.style.cssText = 'font-size:11px;color:#a6adc8;margin-bottom:3px';
  lbl.textContent = label;
  row.appendChild(lbl); row.appendChild(createControl());
  panel.appendChild(row);
}

function _addSlider(panel, renderer, label, path, min, max, step) {
  const row = document.createElement('div');
  row.style.marginBottom = '8px';
  const lbl = document.createElement('div');
  lbl.style.cssText = 'display:flex;justify-content:space-between;font-size:11px;color:#a6adc8;margin-bottom:2px';
  const valSpan = document.createElement('span');
  const curVal = _getNestedParam(renderer.getParams(), path) ?? ((min + max) / 2);
  valSpan.textContent = Number(curVal).toFixed(2);
  lbl.innerHTML = `<span>${escapeHtml(label)}</span>`;
  lbl.appendChild(valSpan);
  row.appendChild(lbl);
  const input = document.createElement('input');
  input.type = 'range'; input.min = min; input.max = max; input.step = step;
  input.value = curVal; input.className = 'cine-slider'; input.dataset.getter = path;
  Object.assign(input.style, { width: '100%', accentColor: '#89b4fa', cursor: 'pointer' });
  input.oninput = () => {
    const v = parseFloat(input.value);
    valSpan.textContent = v.toFixed(2);
    const obj = _buildNestedParam(path, v);
    if (path === 'bloom.intensity') obj.bloom.enabled = v > 0.01;
    if (path === 'ssao.intensity') obj.ssao.enabled = v > 0.01;
    renderer.setParams(obj);
  };
  row.appendChild(input);
  panel.appendChild(row);
}

function _getNestedParam(obj, path) {
  const keys = path.split('.');
  let cur = obj;
  for (const k of keys) { if (cur == null) return undefined; cur = cur[k]; }
  return cur;
}

function _buildNestedParam(path, value) {
  const keys = path.split('.');
  const obj = {};
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) { cur[keys[i]] = {}; cur = cur[keys[i]]; }
  cur[keys[keys.length - 1]] = value;
  return obj;
}

// ══════════════════════════════════════════════════════════════
// 3. ANNOTATIONS PERSISTENCE
// ══════════════════════════════════════════════════════════════

function _initAnnotationsPersistence() {
  const events = cornerstone.Enums?.Events;
  if (!events) return;
  const target = cornerstone.eventTarget;
  if (!target) return;

  const debouncedSave = () => {
    _hasUnsavedAnnotations = true;
    clearTimeout(_saveTimer);
    _saveTimer = setTimeout(() => {
      const seriesUID = _getCurrentSeries?.();
      if (seriesUID) _saveAnnotations(seriesUID);
    }, 2000);
  };

  const toolEvents = ['ANNOTATION_COMPLETED', 'ANNOTATION_MODIFIED', 'ANNOTATION_REMOVED'];
  const csToolsTarget = window.cornerstoneTools?.eventTarget || target;
  const usePrefix = !!window.cornerstoneTools?.Enums?.Events;
  for (const evtName of toolEvents) {
    const fullName = `CORNERSTONE_TOOLS_${evtName}`;
    try { csToolsTarget.addEventListener(usePrefix ? fullName : evtName, debouncedSave); }
    catch { try { csToolsTarget.addEventListener(usePrefix ? evtName : fullName, debouncedSave); } catch (e) { log.debug('[StudyToolsUI]', e.message); } }
  }

  const vpContainer = document.getElementById('viewport-container');
  if (vpContainer) {
    vpContainer.addEventListener('pointerup', () => { setTimeout(debouncedSave, 500); }, { passive: true });
  }

  window.addEventListener('beforeunload', () => {
    if (_saveTimer) {
      clearTimeout(_saveTimer);
      const seriesUID = _getCurrentSeries?.();
      if (seriesUID) {
        try {
          // [12.0.2] Тот же фильтр, что в _saveAnnotations: в запись серии —
          // только ЕЁ рукотворные аннотации. Прежде здесь выгружалась вся
          // память, и запись при закрытии заражалась чужими сериями.
          const { свои } = _аннотацииСерии(seriesUID);
          if (свои.length || _серииВосстановлены.has(seriesUID)) {
            dbSaveAnnotations(seriesUID, JSON.stringify(свои));
          }
        } catch { /* best effort */ }
      }
    }
    // [11.1.14] УДЕРЖАНИЕ ОКНА СНЯТО.
    //
    // Здесь стояло `if (_hasUnsavedAnnotations) { e.preventDefault();
    // e.returnValue = ''; }`. Флаг взводится на КАЖДОЕ касание инструмента
    // (:578), а снимается только после успешной записи в базу (:620) —
    // значит любой сбой записи оставлял его взведённым навсегда, и программа
    // держала окно, считая, что у врача есть несохранённая работа. В
    // настольной оболочке хуже, чем в браузере: main.cjs перехват не
    // обрабатывает, диалога может не быть вовсе — крестик просто перестаёт
    // закрывать программу. Владелец наблюдал ровно это.
    //
    // Сохранение никуда не делось: строки выше пишут аннотации в базу на том
    // же beforeunload. Держать врача в программе ради записи «по возможности»
    // нельзя — цена ошибки здесь односторонняя: незакрывающееся окно человек
    // обойти не может, а аннотация восстанавливается из базы.
  });

  // [12.0.2] Подметание — через 8 секунд после старта, когда загрузка и
  // восстановление уже отшумели. Не при первом же кадре: старт и так занят.
  setTimeout(() => { подместиСохранённыеАннотации().catch((e) => log.debug('[Annotations] подметание:', e.message)); }, 8000);
}

// ══════════════════════════════════════════════════════════════
// [12.0.2] ПОДМЕТАНИЕ ВСЕЙ БАЗЫ АННОТАЦИЙ, а не только открытой серии.
//
// Чистка в autoLoadAnnotations (12.0.0) лечит запись В МОМЕНТ ОТКРЫТИЯ серии.
// Но раздутые записи ДРУГИХ серий так и лежат в базе — у владельца это сотни
// мегабайт мёртвого груза, которые к тому же целиком читались прежней
// статистикой хранилища. Подметание проходит по всем записям и переписывает
// те, что не пролезают в предел строки; остальных не касается вовсе.
//
// Память: записи берутся ПО ОДНОЙ (ключи — отдельным дешёвым запросом), и
// раздутая строка живёт лишь на время своего просеивания; сито разбирает её
// поэлементно, без JSON.parse целиком (annotationsGuard, пункт 1 в шапке).
// Между записями — уступка главному потоку, чтобы не подвесить интерфейс.
// ══════════════════════════════════════════════════════════════
const ПРЕДЕЛ_СТРОКИ_ЗАПИСИ = 4 * 1024 * 1024;   // 4 МБ ≈ тысячи измерений — столько не рисуют

export async function подместиСохранённыеАннотации() {
  let прибрано = 0, былоШтук = 0, осталосьШтук = 0, былоМб = 0;
  try {
    const ключи = await getAnnotationKeys();
    for (const uid of (ключи || [])) {
      let rec = null;
      try { rec = await getAnnotationRecord(uid); } catch (e) { continue; }
      const строка = rec?.data;
      if (typeof строка !== 'string' || строка.length <= ПРЕДЕЛ_СТРОКИ_ЗАПИСИ) continue;
      // Свежее сито БЕЗ памяти сеанса: подметание судит запись саму по себе
      // и не должно выкидывать аннотации лишь потому, что они сейчас открыты.
      const сито = просеятьАннотации(строка, { потолок: ПОТОЛОК_АННОТАЦИЙ, хранитсяЛи: хранитсяЛиАннотация });
      if (!сито.массив) continue;
      try {
        await dbSaveAnnotations(uid, JSON.stringify(сито.годные), rec.studyId ?? null);
      } catch (eЗ) { log.warn('[Annotations] подметание: запись не переписать —', eЗ.message); continue; }
      прибрано++;
      былоШтук += сито.всего;
      осталосьШтук += сито.годные.length;
      былоМб += строка.length / (1024 * 1024);
      await new Promise((р) => setTimeout(р, 0));
    }
    if (прибрано) {
      log.info(`[Annotations] подметание базы: переписано записей ${прибрано}, `
        + `было ${былоШтук} аннотаций (~${былоМб.toFixed(0)} МБ), осталось ${осталосьШтук}`);
      showToast(`Хранилище аннотаций прибрано: в ${прибрано} ${прибрано === 1 ? 'записи' : 'записях'} `
        + `было ${былоШтук.toLocaleString('ru-RU')} пометок (следствие ошибки размножения), `
        + `оставлено ${осталосьШтук.toLocaleString('ru-RU')} настоящих. Измерения не пострадали`, 'success', 12000);
    } else {
      log.debug('[Annotations] подметание базы: раздутых записей нет');
    }
  } catch (e) {
    log.warn('[Annotations] подметание базы не прошло:', e.message);
  }
  return { прибрано, было: былоШтук, осталось: осталосьШтук };
}

/**
 * [11.1.89] ВТОРОЙ КОНЕЦ ТОЙ ЖЕ ТРУБЫ.
 *
 * Размножение аннотаций шло по кругу: восстановление кладёт копию в память
 * (запрет теперь стоит в toolManager.importAnnotations), а ЭТА функция выгружает
 * всё, что в памяти, и пишет обратно в базу — то есть закрепляет удвоение. Даже
 * с запретом на входе испорченная запись в базе владельца останется испорченной
 * навсегда, если её не переписать.
 *
 * Поэтому здесь ДВЕ вещи.
 *
 * 1. ПОТОЛОК ПРИ ЗАПИСИ. Больше пяти тысяч аннотаций на серию человек не
 *    рисует. Такое число означает не работу врача, а размножение — и писать его
 *    в базу нельзя, иначе следующая сессия его прочтёт.
 * 2. ЖАЛОБА ВСЛУХ. Прежде отказ записи уходил строкой «Auto-save error: Invalid
 *    string length» в журнал — и всё. Владелец видел эту строку и не мог знать,
 *    что его работа НЕ СОХРАНЯЕТСЯ. Молча терять разметку нельзя.
 */
const ПОТОЛОК_АННОТАЦИЙ = 5000;
let _жаловалисьНаРазмножение = false;

/**
 * [12.0.2] ВЛАДЕНИЕ СЕРИИ: чем опознаём «свои» аннотации.
 *
 * Память аннотаций общая на программу и при смене серии НЕ очищается (в
 * сравнении открыты две серии разом — очищать нельзя). Прежде автосохранение
 * выгружало ВСЁ подряд под ключ текущей серии, и измерения серии А попадали
 * в запись серии Б — записи взаимно заражались и росли. Это и был последний
 * незакрытый ход размножения: защита от повторов (11.1.89) держит одну
 * запись, но не мешает записям обмениваться содержимым.
 */
function _владениеСерии(seriesUID) {
  let кадры = null;
  try {
    const ids = _getSeriesImageIdsFn?.(seriesUID) || [];
    if (Array.isArray(ids) && ids.length) кадры = new Set(ids);
  } catch (e) { log.debug('[Annotations] кадры серии не прочесть:', e.message); }
  let frameOfReference = null;
  try {
    if (кадры) {
      const первый = кадры.values().next().value;
      frameOfReference = cornerstone.metaData.get('imagePlaneModule', первый)?.frameOfReferenceUID || null;
    }
  } catch (e) { /* нет метаданных — опознаем по кадрам и тому */ }
  return { кадры, том: `volume_${seriesUID}`, frameOfReference };
}

/** Рукотворные аннотации ИМЕННО ЭТОЙ серии — то единственное, что кладётся в её запись. */
function _аннотацииСерии(seriesUID) {
  const все = getAnnotations?.() || [];
  const владение = _владениеСерии(seriesUID);
  const свои = [];
  let чужих = 0, служебных = 0;
  for (const а of все) {
    if (!хранитсяЛиАннотация(а?.metadata?.toolName)) { служебных++; continue; }
    if (!аннотацияПринадлежитСерии(а, владение)) { чужих++; continue; }
    свои.push(а);
  }
  return { свои, чужих, служебных };
}

/**
 * [12.0.2] Серии, для которых восстановление из базы в ЭТОМ сеансе уже
 * прошло. Нужно для честной записи пустоты: писать «[]» можно только когда
 * мы ТОЧНО знаем, что у серии ничего нет, — а не когда её аннотации ещё не
 * успели восстановиться (autoLoadAnnotations асинхронен, а автосохранение
 * может сработать по pointerup раньше него).
 */
const _серииВосстановлены = new Set();

async function _saveAnnotations(seriesUID) {
  try {
    const { свои, чужих, служебных } = _аннотацииСерии(seriesUID);
    if (свои.length > ПОТОЛОК_АННОТАЦИЙ) {
      log.warn(`[Annotations] у серии ${свои.length} аннотаций — это размножение, а не работа `
        + '(см. пояснение в toolManager.importAnnotations). В базу НЕ пишу, чтобы не закрепить');
      if (!_жаловалисьНаРазмножение) {
        _жаловалисьНаРазмножение = true;
        showToast(`В памяти ${свои.length.toLocaleString('ru-RU')} аннотаций — столько не рисуют, `
          + 'это следствие ошибки размножения (исправлена в 11.1.89). Автосохранение аннотаций '
          + 'приостановлено, чтобы не закрепить порчу. Нажмите «Очистить аннотации» — после этого '
          + 'сохранение заработает как обычно', 'warning', 25000);
      }
      return;
    }
    if (чужих || служебных) {
      log.debug(`[Annotations] в запись серии не попали: чужих серий ${чужих}, служебных ${служебных}`);
    }
    // [12.0.2] Пустота ПИШЕТСЯ, а не пропускается — но только для серии, чьё
    // восстановление уже прошло. Прежде «[]» просто не сохранялся, и стёртые
    // врачом измерения воскресали из старой записи при следующем открытии.
    if (!свои.length && !_серииВосстановлены.has(seriesUID)) { _hasUnsavedAnnotations = false; return; }
    await dbSaveAnnotations(seriesUID, JSON.stringify(свои));
    _hasUnsavedAnnotations = false;
    _жаловалисьНаРазмножение = false;
  } catch (e) {
    // [11.1.89] Не только в журнал: врач должен знать, что его работа не легла.
    log.warn('[Annotations] Auto-save error:', e.message);
    showToast('Аннотации НЕ сохранились: ' + e.message
      + '. Работа в памяти есть, но при закрытии программы пропадёт', 'error', 15000);
  }
}

export async function autoLoadAnnotations(seriesUID) {
  if (!seriesUID) return false;
  try {
    const json = await dbLoadAnnotations(seriesUID);
    if (!json) { _серииВосстановлены.add(seriesUID); return false; }
    const и = importAnnotations(json);
    // [12.0.2] Теперь автосохранению можно верить пустоте этой серии:
    // восстановление прошло, и «в памяти ничего» значит «ничего и нет».
    _серииВосстановлены.add(seriesUID);
    log.debug(`[Annotations] Restored for ${seriesUID.substring(0, 20)}...`);

    // ══════════════════════════════════════════════════════════════
    // [12.0.0] ДОЧИЩАЕМ БАЗУ СРАЗУ, А НЕ «КОГДА-НИБУДЬ».
    //
    // ПОВОД — вопрос владельца: «мы решили вопрос с большим количеством якобы
    // ненужных аннотаций?» Не до конца, и вот чем я себя обманул.
    //
    // В 11.1.89 я поставил запрет на размножение и написал в пояснении:
    // «повторной записью база очистится». Проверил сейчас — НЕ ОЧИСТИТСЯ.
    // Запись в базу происходит только по событию рисования: врач провёл линейку,
    // через две секунды сохранение. Если он ничего не рисует, испорченная запись
    // на 245 767 штук лежит в базе вечно, и каждое открытие серии читает её
    // заново. В его журнале это видно дважды: «повторов пропущено 245 755» и
    // «245 763».
    //
    // Запрет спасал ПАМЯТЬ (в неё ложилось 12 записей вместо четверти миллиона),
    // но диск не лечил. Теперь: увидели, что запись раздута — переписали её
    // тут же тем, что осталось после чистки.
    // ══════════════════════════════════════════════════════════════
    // [12.0.2] Признак раздутости уточнён. Прежнее условие «всего > внесено»
    // срабатывало и на ЧИСТОЙ записи при повторном открытии серии за сеанс
    // (все её аннотации уже в памяти, внесено = 0) — запись зря переписывалась,
    // а врач получал пугающее сообщение о чистке. Раздутость — это когда в
    // самой записи есть мусор: повторы ВНУТРИ записи, служебные аннотации
    // (перекрестья и т.п.) или счёт сверх потолка. Повторы С ПАМЯТЬЮ признаком
    // порчи не являются — сито различает эти два вида (annotationsGuard.js).
    if (и && (и.повторовВЗаписи > 0 || и.служебных > 0 || и.всего > ПОТОЛОК_АННОТАЦИЙ)) {
      const годныхВЗаписи = и.внесено + и.повторовСПамятью;
      log.warn(`[Annotations] запись серии раздута: было ${и.всего}, годных ${годныхВЗаписи} `
        + `(повторов в записи ${и.повторовВЗаписи}, служебных ${и.служебных}) — перезаписываю базу очищенным набором`);
      try {
        // Чистый набор — аннотации ИМЕННО ЭТОЙ серии, а не всё содержимое
        // памяти: иначе сюда попали бы измерения другой открытой серии, и
        // чистка сама же заражала бы запись (та самая дыра, что раздула базу).
        const { свои } = _аннотацииСерии(seriesUID);
        await dbSaveAnnotations(seriesUID, JSON.stringify(свои));
        log.info(`[Annotations] база очищена: осталось ${свои.length} записей`);
        // Говорим один раз за сеанс: врач должен знать, что мусор убран, но
        // повторять это при каждом открытии серии незачем.
        if (!_сказалиПроЧистку) {
          _сказалиПроЧистку = true;
          showToast(`В сохранённых аннотациях этой серии было ${и.всего.toLocaleString('ru-RU')} `
            + `записей — следствие ошибки размножения. Оставлено ${годныхВЗаписи}, база очищена. `
            + 'Ваши измерения не пострадали: убраны только точные копии и служебные пометки', 'success', 15000);
        }
      } catch (eЗ) {
        log.warn('[Annotations] очищенный набор записать не вышло:', eЗ.message);
      }
    }
    return true;
  } catch (e) { log.warn('[Annotations] Auto-load error:', e.message); return false; }
}
let _сказалиПроЧистку = false;

export function forceSaveAnnotations() {
  const seriesUID = _getCurrentSeries?.();
  if (!seriesUID) { showToast('⚠️ Нет загруженной серии', 'warning'); return; }
  _saveAnnotations(seriesUID);
  showToast('💾 Аннотации сохранены', 'success', 1500);
}

// ══════════════════════════════════════════════════════════════
// 4. SERIES THUMBNAILS
// ══════════════════════════════════════════════════════════════

const THUMB_SIZE = 64;

/**
 * [12.0.11] Сколько ПИКСЕЛЕЙ рисовать в превью при показе клеткой 64.
 *
 * В 12.0.10 стояло жёсткое 128 «для резкости на High-DPI» — и это была
 * ошибка, поймана владельцем на снимках: на экране с масштабом 100%
 * (devicePixelRatio 1) браузер ПЕРЕЖИМАЛ 128 обратно в 64 своим билинейным
 * уменьшением, и каждая миниатюра выходила мягче прежней прямой 64×64.
 * Правильно — рисовать ровно столько, сколько экран покажет физически:
 * 64·dpr, один к одному, без пережатия. Потолок 128: выше двукратного
 * масштаба выигрыша уже не разглядеть, а база растёт.
 */
export function _размерПревью(dpr) {
  const к = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  return Math.max(THUMB_SIZE, Math.min(128, Math.round(THUMB_SIZE * к)));
}

/**
 * [12.0.11] Ужать центральный квадрат кадра в N×N Катмуллом-Ромом.
 *
 * История в двух шагах. До 12.0.10 на точку миниатюры бралась ОДНА ближайшая
 * исходная точка: гладкую МР это прощало, шумную КТ — нет (зерно + JPEG =
 * муть). В 12.0.10 я заменил это боксовым усреднением с нерезкой маской — шум
 * ушёл, но ушла и резкость: бокс мылит настоящие границы, маска возвращала их
 * лишь отчасти, и владелец увидел «смазанные» превью уже и у МР.
 *
 * Катмулл-Ром с ядром, растянутым под шаг уменьшения, делает обе работы сразу
 * и лучше: широкая опора гасит шум (как усреднение), а отрицательные лепестки
 * ядра сами подчёркивают границы (как маска, только без её ватности). Это
 * стандартное «бикубическое» уменьшение фотографий. Веса нормируются на
 * сумму, поэтому ровное поле проходит без искажений, а линейный градиент —
 * точно (свойства ядра; сторожатся исполнением в verify_12_0_10).
 *
 * Чистая функция без DOM. Rescale (наклон, сдвиг) применяется к результату —
 * оба линейны, порядок безразличен, а умножений так меньше.
 */
export function _ужатьКатмуллРом(пиксели, ш, сдвигX, сдвигY, сторона, N, наклон, сдвиг) {
  const cr = (t) => {
    t = Math.abs(t);
    if (t <= 1) return (1.5 * t - 2.5) * t * t + 1;
    if (t < 2) return ((-0.5 * t + 2.5) * t - 4) * t + 2;
    return 0;
  };
  const k = сторона / N;                       // шаг уменьшения, ≥1
  const растяжка = Math.max(1, k);             // ядро растягиваем только при уменьшении
  const радиус = 2 * растяжка;

  // Веса одинаковы для всех строк (и столбцов) — считаем один раз на выход.
  const опоры = new Array(N);
  for (let o = 0; o < N; o++) {
    const центр = (o + 0.5) * k - 0.5;
    const от = Math.max(0, Math.ceil(центр - радиус));
    const до = Math.min(сторона - 1, Math.floor(центр + радиус));
    const веса = new Float32Array(до - от + 1);
    let сумма = 0;
    for (let s = от; s <= до; s++) { const в = cr((s - центр) / растяжка); веса[s - от] = в; сумма += в; }
    if (Math.abs(сумма) < 1e-9) { веса.fill(0); веса[Math.min(веса.length - 1, Math.max(0, Math.round(центр) - от))] = 1; сумма = 1; }
    for (let i = 0; i < веса.length; i++) веса[i] /= сумма;
    опоры[o] = { от, веса };
  }

  // Проход 1: по X. врем[y][ox], y — строка исходного квадрата.
  const врем = new Float32Array(сторона * N);
  for (let y = 0; y < сторона; y++) {
    const ряд = (сдвигY + y) * ш + сдвигX;
    const выхРяд = y * N;
    for (let ox = 0; ox < N; ox++) {
      const { от, веса } = опоры[ox];
      let v = 0;
      for (let i = 0; i < веса.length; i++) v += пиксели[ряд + от + i] * веса[i];
      врем[выхРяд + ox] = v;
    }
  }
  // Проход 2: по Y — теми же опорами (квадрат, шаг одинаков).
  const поле = new Float32Array(N * N);
  for (let oy = 0; oy < N; oy++) {
    const { от, веса } = опоры[oy];
    for (let ox = 0; ox < N; ox++) {
      let v = 0;
      for (let i = 0; i < веса.length; i++) v += врем[(от + i) * N + ox] * веса[i];
      поле[oy * N + ox] = v * наклон + сдвиг;
    }
  }
  return поле;
}

// [12.0.11] Надгробие: здесь одну версию (12.0.10) прожили _усреднитьВКвадрат
// и _подчеркнутьКрая — боксовое усреднение с нерезкой маской. Шум КТ они
// погасили, но замылили настоящие границы, и владелец увидел «смазанные»
// превью уже и у МР. Заменены одним _ужатьКатмуллРом выше: его растянутое
// ядро гасит шум КАК усреднение, а отрицательные лепестки подчёркивают
// границы КАК маска — без её ватности.

/**
 * [11.0.2] ПРЕВЬЮ ИЗ ДАННЫХ, А НЕ С ЭКРАНА.
 *
 * ПОВОД — прямая претензия владельца, и она по существу: «в серии может быть
 * голова, грудная клетка, брюшная полость, органы малого таза, и превью должно
 * отражать эту серию картинкой для наиболее быстрого ориентира».
 *
 * Как было. Единственный путь создания миниатюры (generateThumbnailForCurrent)
 * снимал КАНВУ ВЬЮПОРТА. Значит картинка появлялась только у той серии, которую
 * уже открыли, — а список серий существует ровно для того, чтобы решить, какую
 * открывать. Инструмент выбора требовал сначала сделать выбор.
 *
 * Как стало. Берём СРЕДНИЙ СРЕЗ серии и декодируем его отдельно от вьюпорта —
 * тем же загрузчиком Cornerstone, каким пользуются quantTools и echoRegTools.
 * Никакое окно открывать не нужно.
 *
 * Три решения, которые важно знать.
 *
 * 1. СРЕДНИЙ СРЕЗ, а не первый. Первый срез — почти всегда край объёма: воздух
 *    над макушкой, стол под таза́ми, локализатор. Ориентира в нём нет. Середина
 *    показывает анатомию.
 *
 * 2. ОКНО БЕРЁТСЯ ИЗ ТЕГОВ, а если их нет — считается по самому срезу
 *    (2-й и 98-й процентили яркости). У МРТ единиц Хаунсфилда не существует, и
 *    единое окно на все серии дало бы то чёрный, то белый квадрат. Крайние
 *    проценты отбрасываются: иначе одна пересвеченная точка сожмёт всю шкалу.
 *
 * 3. ПО ОДНОЙ СЕРИИ ЗА РАЗ, в простое браузера. Тридцать серий, декодированных
 *    разом, подвесили бы интерфейс в тот самый момент, когда врач листает
 *    список. Очередь идёт по одной, между сериями отдаёт управление, и первым
 *    делом пропускает те, у кого превью уже есть.
 */
async function _превьюИзСреза(seriesUID) {
  const imageIds = _getSeriesImageIdsFn?.(seriesUID) || [];
  if (!imageIds.length) return false;

  // Середина серии: там анатомия, а не край объёма.
  const imageId = imageIds[Math.floor(imageIds.length / 2)];
  const image = await cornerstone.imageLoader.loadAndCacheImage(imageId);
  const пиксели = image.getPixelData();
  const ш = image.columns, в = image.rows;
  if (!пиксели?.length || !ш || !в) return false;

  // Окно: сперва из тегов, но с ПРОВЕРКОЙ результата.
  //
  // [11.0.3] Проверка появилась после чёрного квадрата у владельца. Окно из
  // тегов не обязано попадать в данные: у обзорных снимков (топограмм) и у
  // серий с необычным Rescale оно уводит всю картинку за край шкалы, и
  // миниатюра выходит равномерно чёрной или белой. Поэтому: посчитали —
  // посмотрели на разброс яркости — если картинка вышла плоской, пересчитали
  // окно по самому срезу. Врать чёрным квадратом хуже, чем не показать ничего.
  const наклон = Number(image.slope ?? 1) || 1;
  const сдвиг = Number(image.intercept ?? 0) || 0;
  const первое = (v) => (Array.isArray(v) ? v[0] : v);
  let центр = Number(первое(image.windowCenter));
  let ширина = Number(первое(image.windowWidth));

  /**
   * Окно по самому срезу — СЧИТАЯ ТОЛЬКО ТКАНЬ, а не воздух вокруг пациента.
   *
   * [11.0.3] Первая версия брала 2-й и 98-й проценты по ВСЕМУ кадру. На голове
   * это сходило: череп и мозг занимают почти весь кадр. А на срезах тела
   * бо́льшая часть кадра — воздух (−1000 HU), и нижний конец окна садился на
   * него. Мягкие ткани (+50 HU) оказывались почти у верхнего края шкалы, и
   * миниатюры живота выходили белыми блинами — ровно то, что владелец увидел
   * на снимке: чёрный фон и белое тело без единой детали внутри.
   *
   * Поэтому воздух из расчёта исключается. Порог −300 HU: ниже него только
   * воздух и просвет лёгких, выше — жир, вода, мышца, кость. Если после
   * отсева ничего не осталось (обзорный снимок, МР без единиц Хаунсфилда) —
   * считаем по всему кадру: это лучше, чем ничего.
   *
   * И второе, не менее важное. Окно ставится НЕ от края до края, а ВОКРУГ
   * ПРЕОБЛАДАЮЩЕЙ ТКАНИ: центр — середина выборки, полуширина — большее из
   * расстояний до 5-го и 95-го процентов. Простая пара «низ…верх» проваливала
   * голову: там мозга в кадре втрое больше, чем кости, нижний конец садился
   * ровно на мозг, и он уходил в чёрное. При центрировании по середине и мозг,
   * и мышца живота попадают в середину шкалы, а кость и контраст — в светлое.
   * Проверено на моделях срезов в tests/thumbnail_test.mjs.
   */
  const окноПоСрезу = () => {
    const шаг = Math.max(1, Math.floor(пиксели.length / 20000));
    const всё = [];
    for (let i = 0; i < пиксели.length; i += шаг) всё.push(пиксели[i] * наклон + сдвиг);
    всё.sort((a, b) => a - b);
    // Шкала Хаунсфилда узнаётся по воздуху: если его нет, отсеивать нечего.
    const этоHU = всё[0] < -500;
    const ткань = этоHU ? всё.filter((v) => v > -300) : всё;
    const проба = ткань.length >= 32 ? ткань : всё;
    const середина = проба[Math.floor(проба.length * 0.50)];
    // Ширина — по МЕЖКВАРТИЛЬНОМУ РАЗМАХУ, а не по краям выборки.
    //
    // [11.0.6] Края обманывают. На голове кость даёт +1200 при мозге +35, и
    // окно «от края до края» растягивалось на 2500 единиц: мозг, кровь и отёк
    // сжимались в пять уровней серого — ровно те безликие серые кружки, что
    // владелец увидел на снимке. Межквартильный размах кость игнорирует: он
    // описывает, как расходится ОСНОВНАЯ масса ткани, а редкие плотные точки на
    // него не влияют. Множитель 5 подобран на моделях среза: меньше — ткань
    // упирается в белое, больше — снова блёкнет.
    const q1 = проба[Math.floor(проба.length * 0.25)];
    const q3 = проба[Math.floor(проба.length * 0.75)];
    const ш = Math.max(60, Math.min(2200, 5 * Math.max(1, q3 - q1)));
    return { центр: середина, ширина: ш };
  };

  if (!isFinite(ширина) || ширина <= 0 || !isFinite(центр)) ({ центр, ширина } = окноПоСрезу());

  // MONOCHROME1 — шкала перевёрнута: ноль это белое. Без учёта получается негатив.
  const негатив = String(image.photometricInterpretation || '').toUpperCase() === 'MONOCHROME1';

  // Рисуем квадрат из центра кадра — так же, как делал прежний генератор,
  // чтобы миниатюры не отличались пропорциями от уже накопленных.
  const сторона = Math.min(ш, в);
  const сдвигX = Math.floor((ш - сторона) / 2), сдвигY = Math.floor((в - сторона) / 2);

  // [12.0.11] Ровно столько пикселей, сколько покажет экран (64·dpr, без
  // браузерного пережатия), и Катмулл-Ром вместо усреднения с маской —
  // подробности у _размерПревью и _ужатьКатмуллРом.
  const N = _размерПревью(typeof window !== 'undefined' ? window.devicePixelRatio : 1);
  const холст = document.createElement('canvas');
  холст.width = N; холст.height = N;
  const ctx = холст.getContext('2d');
  const поле = _ужатьКатмуллРом(пиксели, ш, сдвигX, сдвигY, сторона, N, наклон, сдвиг);

  /** Собрать миниатюру при заданном окне; вернуть разброс и среднюю яркость. */
  const собрать = (ц, шир) => {
    const низОкна = ц - шир / 2;
    const кадр = ctx.createImageData(N, N);
    let мин = 255, макс = 0, сумма = 0;
    for (let i = 0; i < поле.length; i++) {
      let серый = Math.round((поле[i] - низОкна) / шир * 255);
      серый = серый < 0 ? 0 : серый > 255 ? 255 : серый;
      if (негатив) серый = 255 - серый;
      if (серый < мин) мин = серый;
      if (серый > макс) макс = серый;
      сумма += серый;
      const п = i * 4;
      кадр.data[п] = кадр.data[п + 1] = кадр.data[п + 2] = серый;
      кадр.data[п + 3] = 255;
    }
    return { кадр, разброс: макс - мин, средняя: сумма / (N * N) };
  };

  /**
   * Годность картинки. [11.0.3]
   *
   * Сперва проверялся только РАЗБРОС — плоская ли картинка. Этого мало:
   * миниатюры живота у владельца были не плоскими (чёрный фон, белое тело), а
   * разброс в них — полные 255. При этом внутри тела не было видно ничего.
   * Поэтому смотрим и на СРЕДНЮЮ яркость: если кадр в целом ушёл в белое или
   * в чёрное, окно промахнулось, каким бы ни был разброс.
   */
  const годна = (р) => р.разброс >= 12 && р.средняя > 18 && р.средняя < 232;

  let итог = собрать(центр, ширина);
  if (!годна(итог)) {
    // Окно (из тегов или прежнее) промахнулось — считаем по самому срезу.
    const своё = окноПоСрезу();
    const вторая = собрать(своё.центр, своё.ширина);
    // Берём ту, что годна; если обе не годны — ту, где больше разброс.
    if (годна(вторая) || вторая.разброс > итог.разброс) итог = вторая;
  }
  if (итог.разброс < 4) return false;   // и по срезу пусто — срез действительно пустой, чёрный квадрат не рисуем
  ctx.putImageData(итог.кадр, 0, 0);

  // [12.0.10] PNG вместо JPEG 0.7: на 128 пикселях серого JPEG звенел вокруг
  // кости и доваривал усреднённую ткань в кашу. PNG без потерь, миниатюра
  // выходит на 8–20 КиБ — хранилищу это по силам.
  const dataURL = холст.toDataURL('image/png');
  await saveThumbnail(seriesUID, dataURL, КЛЕЙМО_ПРЕВЬЮ);
  // Если строка уже на экране — подменяем заглушку сразу, не дожидаясь
  // следующей перерисовки списка.
  const строка = document.querySelector(`.series-item[data-series-uid="${seriesUID}"] .series-thumb-wrap`);
  if (строка) строка.innerHTML = `<img class="series-thumb" src="${dataURL}" width="${THUMB_SIZE}" height="${THUMB_SIZE}" alt="">`;
  return true;
}

/**
 * [11.0.3] Клеймо нынешнего способа съёмки. Меняется, когда меняется сам
 * способ, — и тогда старые превью пересобираются сами, без «очистить всё».
 * Записи без клейма — снятые с экрана прежним генератором либо сделанные
 * сломанной очередью 11.0.2: и те и другие подлежат пересъёмке.
 */
const КЛЕЙМО_ПРЕВЬЮ = 'срез-v5';   // [12.0.11] v5: Катмулл-Ром в точный размер экрана (64·dpr), PNG (формула окна НЕ менялась — пересборка ради новой отрисовки)

/** Очередь: по одной серии за раз, в простое браузера. */
let _очередьПревью = null;
export async function построитьПревьюВсехСерий(seriesList) {
  if (_очередьПревью) return _очередьПревью;          // уже идёт — второй раз не запускаем
  _очередьПревью = (async () => {
    let сделано = 0, пропущено = 0;
    const причины = [];
    // [11.0.3] Отчёт очереди — на ВИДИМОМ уровне. Прежде и старт, и отказы
    // уходили в debug, которого в консоли не видно, и «превью не появились»
    // было неотличимо от «очередь не запускалась». Теперь по консоли сразу
    // ясно, дошло ли дело до серий и на чём именно споткнулось.
    log.info(`[Превью] очередь пошла, серий: ${(seriesList || []).length}`);
    try {
      for (const s of seriesList || []) {
        const имя = s.seriesDesc || s.modality || s.seriesUID;
        try {
          // Пропускаем только СВОИ превью. Чужие (снятые с экрана) и сделанные
          // сломанной очередью 11.0.2 — пересобираем: иначе чёрный квадрат,
          // однажды попавший в базу, остался бы там навсегда.
          //
          // [11.0.3] С ограничением по времени. Обращение к базе ждёт открытия
          // хранилища, а оно может не открыться вовсе — приватное окно, запрет
          // на IndexedDB, занятая другой вкладкой база. Без ограничения очередь
          // молча встала бы на первой же серии, и снаружи это выглядело бы ровно
          // как «превью не появляются»: ни картинок, ни ошибки.
          const клеймо = await Promise.race([
            getThumbnailSource(s.seriesUID),
            new Promise((r) => setTimeout(() => r('__нет ответа__'), 1000)),
          ]);
          if (клеймо === '__нет ответа__') log.warn(`[Превью] база не ответила про «${имя}» — строю заново`);
          else if (клеймо === КЛЕЙМО_ПРЕВЬЮ) continue;
          if (await _превьюИзСреза(s.seriesUID)) сделано++;
          else { пропущено++; причины.push(`${имя}: срез не дал картинки`); }
        } catch (e) {
          // Одна серия без превью — не повод бросать остальные: у неё может не
          // быть пикселей вовсе (SR, RTSTRUCT, PR), и это нормально.
          пропущено++;
          причины.push(`${имя}: ${e.message}`);
        }
        await _передышка();
      }
      log.info(`[Превью] построено: ${сделано}, пропущено: ${пропущено}`);
      if (причины.length) log.warn('[Превью] почему пропущены:\n  ' + причины.slice(0, 12).join('\n  '));
      // [12.0.11] Отказы — НА ЭКРАН, а не только в консоль. У владельца серия
      // dwi_ADC осталась без превью вовсе, и снаружи это было неотличимо от
      // «программа не пыталась»: консоль врач не открывает. Тост называет
      // число и первую причину — по ней видно, куда смотреть.
      if (пропущено > 0) {
        try {
          showToast(`Превью не построились у ${пропущено} сер.: ${причины[0] || 'причина в консоли'}`, 'warning', 6000);
        } catch (e) { /* тост — не повод ронять очередь */ }
      }
    } catch (e) {
      // Сюда попадает сбой САМОГО цикла — ровно тот случай, что убил очередь в
      // 11.0.2 (отвязанный requestIdleCallback). Молчать о нём нельзя.
      log.error('[Превью] очередь оборвалась:', e.message);
    } finally {
      // [11.0.3] finally обязателен. Без него любой сбой посреди очереди
      // оставлял бы _очередьПревью занятой навсегда, и повторная отрисовка
      // списка молча ничего бы не делала: «уже идёт» — а не идёт ничего.
      _очередьПревью = null;
    }
  })();
  return _очередьПревью;
}

/**
 * Отдать управление браузеру между сериями.
 *
 * [11.0.3] ЗДЕСЬ БЫЛА ОШИБКА, ИЗ-ЗА КОТОРОЙ ПРЕВЬЮ НЕ ПОЯВЛЯЛИСЬ. Стояло:
 *
 *     await new Promise((r) => (window.requestIdleCallback || setTimeout)(r, 1));
 *
 * Скобки берут МЕТОД, оторванный от `window`, и зовут его сам по себе.
 * Браузерные функции так звать нельзя — Firefox и Chrome отвечают «Illegal
 * invocation». Исключение возникало ВНЕ разбора по сериям (тот `try` охватывал
 * только саму серию) и роняло весь цикл после ПЕРВОЙ же серии. Отсюда и картина
 * у владельца: один чёрный квадрат сверху — первая серия успела, — и заглушки
 * у всех остальных.
 *
 * Ни одна из 96 проверок этого поймать не могла: node не исполняет код
 * интерфейса, а синтаксис здесь безупречный. Второй случай того же рода за два
 * дня, после `_ac is not defined`.
 */
function _передышка() {
  return new Promise((r) => {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(() => r(), { timeout: 200 });
    else setTimeout(r, 1);
  });
}

export async function renderSeriesListWithThumbnails(seriesList, onSeriesClick) {
  const el = document.getElementById('series-list');
  if (!el) return;
  el.innerHTML = '';
  let thumbCache = new Map();
  try { thumbCache = await getAllThumbnails(); } catch (e) { log.debug("[studyToolsUI]", e.message); }

  // [10.7.0] Когда загружено два исследования, помечаем, чья это серия.
  //
  // Раньше пометка была не нужна: программа держала одно исследование, и все
  // серии в списке были его. Теперь их может быть два, а список плоский — и
  // без даты рядом врач примет прошлогоднюю МРТ за часть сегодняшней КТ.
  // В динамике такая путаница опаснее пустого окна.
  //
  // Пометка появляется ТОЛЬКО когда исследований больше одного: в обычной
  // работе лишняя строчка в узком сайдбаре ничего не даёт.
  const помечать = buildStudyIndex(seriesList).size > 1;

  seriesList.forEach((s, idx) => {
    const imgCount = (s.images || s.files || []).length;
    const item = document.createElement('div');
    item.className = 'series-item';
    item.dataset.seriesUid = s.seriesUID;
    const thumbSrc = thumbCache.get(s.seriesUID);
    const thumbHTML = thumbSrc
      ? `<img class="series-thumb" src="${thumbSrc}" width="${THUMB_SIZE}" height="${THUMB_SIZE}" alt="">`
      : `<div class="series-thumb series-thumb-placeholder">${escapeHtml(s.modality) || '?'}</div>`;
    item.innerHTML = `
      <div class="series-thumb-wrap">${thumbHTML}</div>
      <div class="series-info-wrap">
        <span class="series-modality">${escapeHtml(s.modality) || '—'}</span>
        <div class="series-desc">${escapeHtml(s.seriesDesc) || 'Series ' + (idx + 1)}</div>
        <div class="series-count">${imgCount} img</div>
        ${помечать ? `<div class="series-study">${escapeHtml(formatStudyDate(s.studyDate) || 'без даты')}</div>` : ''}
      </div>`;
    item.addEventListener('click', () => {
      el.querySelectorAll('.series-item').forEach(i => i.classList.remove('active'));
      item.classList.add('active');
      if (onSeriesClick) onSeriesClick(s.seriesUID);
    });
    el.appendChild(item);
  });

  // [11.0.2] Список нарисован — достраиваем недостающие превью из данных.
  // Не ждём: очередь идёт своим ходом, по одной серии, и подменяет заглушки
  // по мере готовности. Список листается всё это время.
  построитьПревьюВсехСерий(seriesList);
}

/** [6.4.1] Подсветить активную серию по UID — без клика.
 *  Вызывается при автозагрузке и любой смене серии, чтобы панель всегда
 *  показывала, какая серия сейчас на экране. */
export function highlightActiveSeries(seriesUID) {
  const apply = () => {
    const el = document.getElementById('series-list');
    if (!el) return false;
    const item = el.querySelector(`.series-item[data-series-uid="${seriesUID}"]`);
    if (!item) return false;
    el.querySelectorAll('.series-item').forEach(i => i.classList.remove('active'));
    item.classList.add('active');
    item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    return true;
  };
  // Список серий рендерится асинхронно (миниатюры) — если он ещё не готов,
  // повторяем несколько раз с нарастающей задержкой
  if (apply()) return;
  [150, 500, 1200, 2500].forEach(ms => setTimeout(apply, ms));
}

export function generateThumbnailForCurrent(seriesUID, viewportId) {
  if (!seriesUID) return;
  setTimeout(async () => {
    try {
      const re = getRenderingEngine();
      if (!re) return;
      const vpId = viewportId || _findBestViewportId(re);
      if (!vpId) return;
      const vp = re.getViewport(vpId);
      if (!vp) return;
      const canvas = vp.canvas || vp.element?.querySelector('canvas');
      if (!canvas || canvas.width === 0) return;
      const existing = await getThumbnail(seriesUID);
      if (existing) return;
      const thumbCanvas = document.createElement('canvas');
      // [12.0.11] Тот же размер, что у основного генератора: ровно 64·dpr —
      // на масштабе 100% это 64 один к одному, без браузерного пережатия
      // (жёсткое 128 из 12.0.10 на dpr=1 мылилось при показе).
      const N = _размерПревью(typeof window !== 'undefined' ? window.devicePixelRatio : 1);
      thumbCanvas.width = N; thumbCanvas.height = N;
      const ctx = thumbCanvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      const srcSize = Math.min(canvas.width, canvas.height);
      const sx = (canvas.width - srcSize) / 2;
      const sy = (canvas.height - srcSize) / 2;
      ctx.drawImage(canvas, sx, sy, srcSize, srcSize, 0, 0, N, N);
      const dataURL = thumbCanvas.toDataURL('image/png');
      // [11.0.3] Клеймо «экран»: такое превью показывает то, что было в окне —
      // с текущим окном плотности и масштабом. Очередь его пересоберёт из
      // среза, чтобы все миниатюры списка были сняты одинаково.
      await saveThumbnail(seriesUID, dataURL, 'экран');
      const item = document.querySelector(`.series-item[data-series-uid="${seriesUID}"]`);
      if (item) {
        const wrap = item.querySelector('.series-thumb-wrap');
        if (wrap) wrap.innerHTML = `<img class="series-thumb" src="${dataURL}" width="${THUMB_SIZE}" height="${THUMB_SIZE}" alt="">`;
      }
    } catch (e) { log.warn('[Thumb] Generate error:', e.message); }
  }, 1500);
}

function _findBestViewportId(re) {
  for (const id of ['axial', 'main', 'left', 'topLeft']) {
    if (re.getViewport(id)) return id;
  }
  const viewports = re.getViewports();
  for (const vp of viewports) {
    if (vp.type !== 'volumeViewport3D' && vp.id !== 'volume3d') return vp.id;
  }
  return viewports[0]?.id || null;
}

// ══════════════════════════════════════════════════════════════
// 5. ENTERPRISE FEATURES
// ══════════════════════════════════════════════════════════════

/**
 * [11.0.2] Перестроить ВСЕ превью заново — теперь из данных.
 *
 * Прежняя regenerateAllThumbnails была миной. Она стирала превью и тут же
 * звала generateThumbnailForCurrent, который снимает ТЕКУЩУЮ КАНВУ — ту, где
 * открыта одна конкретная серия. То есть «регенерация всех» присвоила бы всем
 * сериям подряд один и тот же снимок: то, что было на экране в момент нажатия.
 * Это хуже отсутствия превью — врач увидел бы осмысленные картинки, которые
 * лгут о содержимом. Функция ниоткуда не звалась, и это её и спасало.
 *
 * Теперь она делает то, что обещает: стирает старое и строит заново из средних
 * срезов, серия за серией.
 */
export async function regenerateAllThumbnails() {
  const allSeries = _getAllSeriesFn?.() || [];
  if (!allSeries.length) { showToast('Нет загруженных серий', 'warning'); return; }
  for (const s of allSeries) {
    try { await saveThumbnail(s.seriesUID, null); } catch (e) { log.debug('[Превью] очистка:', e.message); }
  }
  showToast(`Строю превью заново: ${allSeries.length} серий`, 'info', 2000);
  await построитьПревьюВсехСерий(allSeries);
  showToast('Превью перестроены', 'success', 1500);
}

/**
 * Clear all cached thumbnails
 */
export async function clearAllThumbnails() {
  const allSeries = _getAllSeriesFn?.() || [];
  for (const s of allSeries) {
    try { await saveThumbnail(s.seriesUID, null); } catch (e) { log.debug("[studyToolsUI]", e.message); }
  }
  // Clear DOM placeholders
  document.querySelectorAll('.series-thumb-wrap').forEach(wrap => {
    wrap.innerHTML = '<div class="series-thumb series-thumb-placeholder">?</div>';
  });
  showToast('🗑 Превью очищены', 'info', 1500);
}

/**
 * Export annotations as CSV for clinical reporting
 */
export function exportAnnotationsCSV() {
  try {
    const anns = getAnnotations?.() || [];
    if (!anns.length) { showToast('⚠️ Нет аннотаций', 'warning'); return; }
    const rows = [['Type', 'Tool', 'Label', 'Mean HU', 'StdDev', 'Area mm²', 'Length mm', 'Slice', 'Timestamp']];
    for (const a of anns) {
      const tool = a.metadata?.toolName || '';
      const label = a.data?.label || '';
      const stats = a.data?.cachedStats || {};
      const firstStats = Object.values(stats)[0] || {};
      rows.push([
        a.annotationType || '',
        tool,
        label,
        firstStats.mean?.toFixed(1) || '',
        firstStats.stdDev?.toFixed(1) || '',
        firstStats.area?.toFixed(2) || '',
        firstStats.length?.toFixed(2) || a.data?.length?.toFixed(2) || '',
        a.metadata?.sliceIndex ?? '',
        new Date().toISOString(),
      ]);
    }
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }); // BOM for Excel
    downloadBlob(blob, `annotations_${Date.now()}.csv`);
    showToast(`📊 CSV: ${anns.length} аннотаций`, 'success');
  } catch (e) { showToast('❌ ' + e.message, 'error'); }
}

/**
 * Toggle cinematic panel (for enterprise panel button)
 */
export function toggleCinematicPanelExport() {
  _toggleCinematicPanel();
}

/**
 * Get cinematic panel state
 */
export function getCinematicPanelState() {
  const renderer = getCinematicRenderer();
  return {
    panelOpen: !!_cinematicPanelEl,
    rendererAvailable: !!renderer,
    enabled: renderer?.isEnabled?.() || false,
    fps: _lastFPS,
    params: renderer?.getParams?.() || null,
    presets: Object.keys(CINEMATIC_PRESETS),
    customPresets: renderer?.getCustomPresets ? Object.keys(renderer.getCustomPresets()) : [],
    isRecording: renderer?.isRecording?.() || false,
  };
}

/**
 * Set cinematic slider value programmatically
 * @param {string} path — e.g. 'bloom.intensity'
 * @param {number} value
 */
export function setCinematicSlider(path, value) {
  const renderer = getCinematicRenderer();
  if (!renderer) { showToast('⚠️ Кинематографический рендеринг не инициализирован', 'warning'); return; }
  const obj = _buildNestedParam(path, value);
  if (path === 'bloom.intensity') obj.bloom.enabled = value > 0.01;
  if (path === 'ssao.intensity') obj.ssao.enabled = value > 0.01;
  renderer.setParams(obj);
  // Update DOM slider if panel open
  if (_cinematicPanelEl) {
    const slider = _cinematicPanelEl.querySelector(`.cine-slider[data-getter="${path}"]`);
    if (slider) {
      slider.value = value;
      const valSpan = slider.parentElement?.querySelector('span:last-child');
      if (valSpan) valSpan.textContent = Number(value).toFixed(2);
    }
  }
  log.debug(`[Cinematic] ${path} = ${value}`);
}

/**
 * Full diagnostics
 */
export function getStudyToolsState() {
  return {
    hasUnsavedAnnotations: _hasUnsavedAnnotations,
    currentSeries: _getCurrentSeries?.() || null,
    cinematicPanel: getCinematicPanelState(),
    loadedSeries: (_getAllSeriesFn?.() || []).length,
    lastLoadedFiles: (_getLastLoadedFiles?.() || []).length,
  };
}

// ══════════════════════════════════════════════════════════════
// 6. CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyStudyToolsUI() {
  if (_cinematicObserver) { _cinematicObserver.disconnect(); _cinematicObserver = null; }
  if (_fpsInterval) { clearInterval(_fpsInterval); _fpsInterval = null; }
  if (_cinematicPanelEl) { _cinematicPanelEl.remove(); _cinematicPanelEl = null; }
  if (_saveTimer) { clearTimeout(_saveTimer); _saveTimer = null; }
  const fab = document.getElementById('cinematic-fab');
  if (fab) fab.remove();
}

// ══════════════════════════════════════════════════════════════
// 7. CSS (injected once)
// ══════════════════════════════════════════════════════════════

(function injectThumbnailStyles() {
  if (document.getElementById('study-tools-styles')) return;
  const style = document.createElement('style');
  style.id = 'study-tools-styles';
  style.textContent = `
    .series-item { display:flex !important; align-items:center; gap:8px; padding:6px 8px !important; cursor:pointer; border-radius:6px; transition:background 0.15s; }
    .series-item:hover { background:rgba(59,130,246,0.12); }
    .series-item.active { background:rgba(59,130,246,0.28); border-left:3px solid #3b82f6; box-shadow:inset 0 0 0 1px rgba(59,130,246,0.4); }
    .series-thumb-wrap { flex-shrink:0; width:${THUMB_SIZE}px; height:${THUMB_SIZE}px; border-radius:4px; overflow:hidden; background:#1e293b; }
    .series-thumb { width:${THUMB_SIZE}px; height:${THUMB_SIZE}px; object-fit:cover; display:block; }
    .series-thumb-placeholder { display:flex; align-items:center; justify-content:center; color:#64748b; font-size:13px; font-weight:600; width:100%; height:100%; }
    .series-info-wrap { flex:1; min-width:0; overflow:hidden; }
    .series-info-wrap .series-desc { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-size:12px; color:#cbd5e1; }
    .series-info-wrap .series-count { font-size:11px; color:#64748b; }
    .series-info-wrap .series-modality { font-size:10px; font-weight:700; padding:1px 5px; border-radius:3px; background:rgba(59,130,246,0.2); color:#60a5fa; }
    #cinematic-panel::-webkit-scrollbar { width:4px; }
    #cinematic-panel::-webkit-scrollbar-thumb { background:#585b70; border-radius:2px; }
    .cine-select { width:100%; padding:5px 8px; border-radius:6px; border:1px solid #585b70; background:#313244; color:#cdd6f4; font-size:12px; outline:none; cursor:pointer; }
    .cine-select:focus { border-color:#89b4fa; }
    .cine-action-btn { flex:1; min-width:48px; padding:7px 4px; border-radius:6px; border:1px solid #585b70; background:#313244; color:#cdd6f4; cursor:pointer; font-size:14px; transition:all 0.15s; }
    .cine-action-btn:hover { background:#45475a; }
    #cinematic-fab:active { transform:scale(0.95) !important; }
    .cine-slider { height:4px; }
    @media (max-width:768px) {
      #cinematic-panel { width:260px !important; right:8px !important; bottom:128px !important; }
      #cinematic-fab { width:42px !important; height:42px !important; bottom:72px !important; right:10px !important; font-size:18px !important; }
    }
  `;
  document.head.appendChild(style);
})();
